import { useData, useSession, orgID } from '@yingbaba/api';
import { ActionDialog, ErrorNotice, Notice, QueryState } from '@yingbaba/ui';
import { datetime } from '@yingbaba/utils';
import type {
  Member,
  Page,
  OwnershipTransfer,
  DeletionRequest,
  RetentionPolicy,
} from '@yingbaba/types';

type Lifecycle = {
  status: string;
  blockers: string[];
  policy: RetentionPolicy;
  transfers: OwnershipTransfer[];
  deletion?: DeletionRequest | null;
};
const identityFields = [
  { name: 'password', label: '当前账号密码', type: 'password', max: 72 },
  { name: 'mfa_code', label: '双因素验证码（已启用时必填）', required: false, max: 32 },
  { name: 'reason', label: '操作原因', type: 'textarea', min: 2, max: 500 },
];
const lifecycleLabels: Record<string, string> = {
  active: '正常使用',
  closing: '注销冷静期',
  closed: '已注销',
  inactive: '已停用',
  deletion_requested: '待删除',
  deleted: '已删除',
};
function RetentionMessage({ row }: { row?: DeletionRequest | null }) {
  if (!row) return null;
  if (row.status === 'withdrawn') return <Notice>申请已撤回，不会按该申请继续注销或删除。</Notice>;
  return (
    <Notice>
      处理日期：{datetime(row.due_at)}。
      {row.status === 'blocked' ? `暂无法处理：${row.block_reason}` : ''}
      {row.legal_hold ? `资料已设置留存保护：${row.hold_reason}` : ''}
      {row.status === 'retained'
        ? '公开资料已清理，合同、交易及争议证据进入受限留存。'
        : '处理完成前会再次核对未结业务。'}
      业务档案不会随账号或组织直接删除，留存期限和删除结果可由平台审核。
    </Notice>
  );
}
export function AccountLifecyclePanel() {
  const q = useData<Lifecycle>('/account/lifecycle');
  return (
    <section className="panel stack">
      <h2>账号注销</h2>
      <ErrorNotice error={q.error} />
      <p className="muted small">
        注销前需交接组织和未完成项目。冷静期 {q.data?.policy?.account_cooling_days || 7}{' '}
        天内可撤销；注销后退出全部设备，清理个人登录资料和头像，合同及资金记录依留存政策保管。
      </p>
      {q.data?.blockers?.map((item) => (
        <p key={item} className="small">
          {item}
        </p>
      ))}
      <RetentionMessage row={q.data?.deletion} />
      {q.data?.status === 'closing' ? (
        <ActionDialog
          title="撤销账号注销"
          trigger="撤销注销申请"
          path="/account/lifecycle/withdraw"
          submitLabel="确认撤销注销"
          fields={identityFields}
          description="验证身份后撤销申请，账号恢复正常使用。"
        />
      ) : (
        <ActionDialog
          title="申请注销账号"
          trigger="申请注销账号"
          intent="destructive"
          submitLabel="进入注销冷静期"
          path="/account/lifecycle/close"
          fields={identityFields}
          description="冷静期内账号进入只读状态，可登录账号设置撤销。冷静期结束后清理登录资料，无法恢复原账号。"
        />
      )}
    </section>
  );
}
export function OrganizationLifecyclePanel() {
  const q = useData<Lifecycle>('/organization/lifecycle');
  const session = useSession();
  const org = session.data?.organizations.find((item) => item.id === orgID());
  const members = useData<Page<Member>>(
    '/workspace/members?page=1&page_size=100',
    org?.role === 'owner',
  );
  const isOwner = org?.role === 'owner';
  const accountActive =
    !session.data?.account.lifecycle_status || session.data.account.lifecycle_status === 'active';
  const status = q.data?.status || 'active';
  const accountID = session.data?.account.id;
  const action = (name: string, label: string, description: string, danger = false) => (
    <ActionDialog
      key={name}
      title={label}
      trigger={label}
      path={`/organization/lifecycle/${name}`}
      fields={identityFields}
      description={description}
      {...(danger
        ? { intent: 'destructive' as const, submitLabel: `确认${label}` }
        : { submitLabel: `确认${label}`, variant: 'secondary' })}
    />
  );
  return (
    <section className="panel stack" style={{ marginTop: 24 }}>
      <div className="row spread wrap">
        <h2>组织管理</h2>
        <span className="tag">{lifecycleLabels[status] || status}</span>
      </div>
      <ErrorNotice error={q.error || members.error} />
      <p className="small muted">
        所有者交接需由对方在 72
        小时内验证身份并确认；原所有者保留管理员角色。停用前须结清交易、退款与争议。停用后公开内容暂时隐藏，工作空间只读，可恢复使用。
      </p>
      <div className="row wrap">
        {accountActive && isOwner && status === 'active' && (
          <>
            <ActionDialog
              title="交接组织所有者"
              trigger="交接所有者"
              path="/organization/lifecycle/ownership"
              submitLabel="发起交接"
              description="请选择现有成员。对方确认后才更换所有者，待确认期间可撤回。"
              fields={[
                {
                  name: 'to_account_id',
                  label: '接任成员',
                  options: (members.data?.items || [])
                    .filter((m) => m.account_id !== accountID)
                    .map((m) => ({ value: m.account_id, label: `${m.name} · ${m.email}` })),
                },
                ...identityFields,
              ]}
            />
            {action(
              'deactivate',
              '停用组织',
              '仍有履约、待核对付款、待结算、退款或争议时不能停用。',
              true,
            )}
          </>
        )}
        {accountActive && isOwner && status === 'inactive' && (
          <>
            {action(
              'reactivate',
              '恢复组织',
              '恢复后可继续使用原组织；主体认证与历史记录保持原有状态。',
            )}
            {action(
              'delete',
              '申请删除组织',
              `公开内容处理期 ${q.data?.policy?.public_content_days || 30} 天，期间可撤销。删除后无法恢复，交易档案进入受限留存。`,
              true,
            )}
          </>
        )}
        {accountActive &&
          isOwner &&
          status === 'deletion_requested' &&
          action('withdraw-deletion', '撤销组织删除', '撤销后组织保持停用，可另行恢复。')}
      </div>
      {isOwner && q.data?.blockers?.length ? <Notice>{q.data.blockers.join('；')}</Notice> : null}
      <RetentionMessage row={q.data?.deletion} />
      <QueryState query={q}>
        {q.data?.transfers?.map((row) => (
          <div className="list-item" key={row.id}>
            <div className="grow">
              <strong>{row.reason}</strong>
              <p className="muted small">
                {row.status === 'pending'
                  ? `待确认 · 截止 ${datetime(row.expires_at)}`
                  : {
                      accepted: '已完成交接',
                      revoked: '已撤回',
                      declined: '已拒绝',
                      expired: '已过期',
                    }[row.status] || row.status}
              </p>
            </div>
            {accountActive &&
              row.status === 'pending' &&
              new Date(row.expires_at).getTime() > Date.now() && (
                <div className="row wrap">
                  {row.to_account_id === accountID && (
                    <>
                      <ActionDialog
                        title="接任组织所有者"
                        trigger="确认接任"
                        path={`/organization/lifecycle/ownership/${row.id}/accept`}
                        fields={identityFields}
                        submitLabel="确认接任所有者"
                        description="确认后你将承担组织管理职责，原所有者转为管理员。"
                      />
                      <ActionDialog
                        title="拒绝交接"
                        trigger="拒绝"
                        variant="secondary"
                        path={`/organization/lifecycle/ownership/${row.id}/decline`}
                        fields={identityFields}
                        submitLabel="确认拒绝"
                        description="拒绝后保留当前所有者，组织权限不会发生变化。"
                      />
                    </>
                  )}
                  {row.from_account_id === accountID && (
                    <ActionDialog
                      title="撤回交接"
                      trigger="撤回"
                      variant="secondary"
                      path={`/organization/lifecycle/ownership/${row.id}/revoke`}
                      fields={identityFields}
                      submitLabel="确认撤回"
                      description="撤回尚未接受的交接申请，当前所有者保持不变。"
                    />
                  )}
                </div>
              )}
          </div>
        ))}
      </QueryState>
    </section>
  );
}
